import {
  call,
  cancelled,
  delay,
  put,
  race,
  select,
  take,
} from 'redux-saga/effects';
import { setExperiment } from '../../actions/app/experiment';
import { createExperimentChannel } from '../../channels';
import Logger from '../../../services/Logger';
import { SERVICE_NAME } from '../../../constants';
import { createReadyStateChannel } from '../../channels/experiment';
import { isNativeSelector } from '../../reducers/app/params';
import { optimizeContainerIdSelector } from '../../reducers/app/configuration';

export const logger = new Logger(`${SERVICE_NAME}:GoogleOptimize`, {
  prefixColor: '#6540b5',
});

export const CHECK_EXPERIMENT_TIMEOUT = 1000;

export function* watchExperiment() {
  let channel;

  try {
    logger.info('listen active experiments');
    channel = yield call(createExperimentChannel);

    while (true) {
      // An error from socketChannel will cause the saga jump to the catch block
      const payload = yield take(channel);

      yield put(setExperiment(payload));
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    /* istanbul ignore next */
    logger.error('Experiment channel failed. Error:', e);
  } finally {
    /* istanbul ignore else  */
    if (yield cancelled()) {
      channel.close();
      logger.info('watchExperiment is cancelled');
    }
  }
}

export function* setupGoogleOptimize() {
  const isNative = yield select(isNativeSelector);
  const containerId = yield select(optimizeContainerIdSelector);

  if (isNative && containerId) {
    /* istanbul ignore next */
    logger.info('initialize dedicated setup');

    let channel;
    try {
      channel = yield call(createReadyStateChannel);

      yield call(loadExperimentSetup, containerId);

      while (true) {
        const { ready, timeout } = yield race({
          ready: take(channel),
          timeout: delay(CHECK_EXPERIMENT_TIMEOUT),
        });

        /* istanbul ignore else  */
        if (ready || timeout) {
          const status = ready ? 'ready' : 'failed by timeout';
          logger.log(`dedicated setup status - ${status}`);
          break;
        }
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      /* istanbul ignore next */
      logger.error('dedicated setup failed', e);
    }
  } else {
    /* istanbul ignore next */
    logger.info('skipping dedicated setup');
  }
}

export function* loadExperimentSetup(containerId) {
  yield call(injectOptimizeAntiFlickerScript, containerId);

  yield call(injectOptimizeScript, containerId);
}

/**
 * Adds async script to <head> with Google Optimize bundle
 * @param containerId - Google Optimize container ID, a.k.a OPT_CONTAINER_ID
 * @link https://support.google.com/optimize/answer/10106536?hl=en&ref_topic=6197443
 */
export function injectOptimizeScript(containerId) {
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googleoptimize.com/optimize.js?id=${containerId}`;
  document.head.appendChild(script);
}

/**
 * Adds style and script to <head>
 * Should be added before Google Optimize bundle script tag
 * @param containerId
 * @link https://support.google.com/optimize/answer/7100284 - Anti-flicker snippet installation
 * @link https://developers.google.com/optimize/devguides/antiflicker - How it works
 */
export const ANTI_FLICKER_STYLE = '.async-hide {opacity: 0 !important}';

export function injectOptimizeAntiFlickerScript(containerId) {
  const style = document.createElement('style');
  style.textContent = ANTI_FLICKER_STYLE;

  const script = document.createElement('script');
  script.text = `(function(a,s,y,n,c,h,i,d,e){s.className+=' '+y;h.start=1*new Date;
h.end=i=function(){s.className=s.className.replace(RegExp(' ?'+y),'');!!window.optimizeReadyCallback&&window.optimizeReadyCallback()};
(a[n]=a[n]||[]).hide=h;setTimeout(function(){i();h.end=null},c);h.timeout=c;
})(window,document.documentElement,'async-hide','dataLayer',4000,
{'${containerId}':true});`;

  document.head.append(style, script);
}
